import { useQuery } from '@apollo/client'
import { Box, Button, Card, CardActions, styled, Typography } from '@mui/material'
import ExternalLink from '@mui/material/Link'
import { useFlag } from '@unleash/proxy-client-react'
import { isMobile } from 'mobile-device-detect'
import { useCallback, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useEffectOnce } from 'react-use'
import { SubscriptionDocument, SubscriptionStatus, UserProfileDocument } from '../../../../../graphql/generated'
import useGAEvent from '../../../../auth/hooks/useGAEvent'
import Payment from '../../../../buckets/components/modals/PaymentModal'
import DowngradeFreePlanModal from '../../../../buckets/components/settings/modals/DowngradeFreePlanModal'
import UpgradeChangeModal from '../../../../buckets/components/settings/modals/UpgradeChangeModal'
import useAlerts from '../../../../buckets/hooks/useAlerts'
import BlackText from '../../../../shared/components/BlackText'
import FeatureList from './FeatureList'

const PlansContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '16px',
  justifyContent: 'center',
  alignItems: 'flex-start',
  marginTop: '10px',
  maxWidth: '1200px',
  margin: '10px auto 0',
  flexWrap: 'wrap',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    alignItems: 'center'
  }
}))

const PlanCardWrapper = styled(Box)(() => ({
  maxWidth: '325px',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center'
}))

const StyledCard = styled(Card)(() => ({
  position: 'relative',
  borderRadius: '16px',
  marginTop: '20px',
  textAlign: 'center',
  border: '2px solid #202CD3',
  overflow: 'visible',
  backgroundColor: '#FFFFFF',
  padding: '24px 16px',
  minHeight: isMobile ? 'auto' : '350px',
  display: 'flex',
  flexDirection: 'column',
  maxWidth: '350px',
  width: '100%'
}))

const CurrentPlanCard = styled(StyledCard)(({ theme }) => ({
  border: `2px solid ${theme.palette.text.disabled}`
}))

const PopularCard = styled(StyledCard)(() => ({
  border: '2px solid #BA09B7',
  zIndex: 2,
  maxWidth: '350px',
  width: '100%',
  minHeight: isMobile ? 'auto' : '380px'
}))

const CardHead = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontSize: '1.5rem',
  fontWeight: 600,
  lineHeight: 1.2,
  marginBottom: '8px',
  marginTop: '0',
  [theme.breakpoints.up('xs')]: {
    fontSize: '1.75rem',
    marginBottom: '8px'
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: '1.75rem'
  }
}))

const CardStorage = styled(Typography)(({ theme }) => ({
  color: '#000000',
  fontSize: '0.875rem',
  fontWeight: 400,
  lineHeight: 1.4,
  textTransform: 'none',
  marginBottom: '16px',
  [theme.breakpoints.up('sm')]: {
    fontSize: '0.875rem',
    lineHeight: 1.4
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: '1rem'
  }
}))

const CardPrice = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontSize: '3.5rem',
  fontWeight: 700,
  lineHeight: 1,
  marginTop: '16px',
  marginBottom: '8px',
  [theme.breakpoints.up('xs')]: {
    fontSize: '3rem'
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '3.5rem'
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: '4rem'
  }
}))

const CardTerm = styled(Typography)(({ theme }) => ({
  color: '#000000',
  fontSize: '0.875rem',
  fontWeight: 400,
  lineHeight: 1,
  margin: '0 0 24px 0',
  [theme.breakpoints.up('lg')]: {
    fontSize: '1rem'
  }
}))

const PaddingActions = styled(CardActions)(({ theme }) => ({
  padding: '0 16px 0px',
  marginTop: 'auto',
  [theme.breakpoints.up('sm')]: {
    padding: '0 16px 0px'
  },
  [theme.breakpoints.up('lg')]: {
    padding: '0 20px 0px'
  }
}))

const StyledButton = styled(Button)(({ theme }) => ({
  background: '#202CD3',
  border: '1px solid #202CD3',
  borderRadius: '86px',
  color: '#FFFFFF',
  fontSize: '1rem',
  fontWeight: 600,
  textTransform: 'none',
  margin: '0 auto',
  padding: '12px 24px',
  width: '100%',
  height: '40px',
  '&:hover': {
    background: '#1A23B8',
    border: '1px solid #1A23B8',
    color: '#FFFFFF'
  },
  [theme.breakpoints.up('sm')]: {
    fontSize: '0.875rem',
    padding: '12px 20px'
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: '1rem',
    padding: '12px 24px'
  }
}))

const PopularButton = styled(StyledButton)(({ theme }) => ({
  background: theme.palette.groupsyPink[700],
  border: '1px solid #BA09B7',
  color: '#FFFFFF',
  '&:hover': {
    background: theme.palette.groupsyPink[800],
    border: '1px solid #BA09B7',
    color: '#FFFFFF'
  }
}))

const CurrentPlan = styled(Button)(({ theme }) => ({
  background: theme.palette.text.disabled,
  borderRadius: '86px',
  color: '#FFFFFF !important',
  fontSize: '1rem',
  fontWeight: 600,
  textTransform: 'none',
  margin: '0 auto',
  padding: '12px 24px',
  width: '100%',
  height: '40px',
  cursor: 'default',
  '&:hover': {
    background: theme.palette.text.disabled,
    border: `1px solid ${theme.palette.text.disabled}`,
    color: '#FFFFFF !important'
  },
  [theme.breakpoints.up('sm')]: {
    fontSize: '0.875rem',
    padding: '12px 20px'
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: '1rem',
    padding: '12px 24px'
  }
}))

const CardSavings = styled(Typography)(({ theme }) => ({
  marginBottom: 0,
  marginTop: '16px',
  padding: '0 16px',
  color: '#374151',
  fontSize: '0.875rem',
  fontWeight: 400,
  lineHeight: 1.4,
  textAlign: 'center',
  '& span': {
    fontWeight: '600',
    color: '#202CD3'
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: '1rem'
  }
}))

const Recommended = styled('p')(({ theme }) => ({
  fontSize: '0.875rem',
  color: '#FFFFFF',
  background: theme.palette.groupsyPink[800],
  borderRadius: '86px',
  fontWeight: 600,
  margin: '0 auto',
  padding: '4px 12px',
  position: 'absolute',
  zIndex: 3,
  width: 'fit-content',
  top: '-17px',
  left: '50%',
  transform: 'translate(-50%, 0)',
  [theme.breakpoints.up('xs')]: {
    fontSize: '0.875rem',
    padding: '4px 12px'
  },
  [theme.breakpoints.up('md')]: {
    padding: '4px 12px'
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: '1rem',
    padding: '6px 16px'
  }
}))

const CurrentPlanRecommended = styled('p')(({ theme }) => ({
  fontSize: '0.875rem',
  color: '#FFFFFF',
  background: '#9CA3AF',
  borderRadius: '86px',
  fontWeight: 600,
  margin: '0 auto',
  padding: '4px 12px',
  position: 'absolute',
  zIndex: 10,
  width: 'fit-content',
  top: '-17px',
  left: '50%',
  transform: 'translate(-50%, 0)',
  boxShadow: '0 0 0 2px #FFFFFF',
  [theme.breakpoints.up('xs')]: {
    fontSize: '0.875rem',
    padding: '4px 12px'
  },
  [theme.breakpoints.up('md')]: {
    padding: '4px 12px'
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: '1rem',
    padding: '6px 16px'
  }
}))

const BlackLink = styled(ExternalLink)(({ theme }) => ({
  marginTop: 12,
  fontWeight: 600,
  display: 'block',
  color: '#202CD3',
  textDecoration: 'none',
  textAlign: 'center',
  fontSize: '0.875rem',
  [theme.breakpoints.down('sm')]: {
    margin: 8
  },
  '&:hover': {
    cursor: 'pointer',
    textDecoration: 'underline'
  }
}))

interface YearlyProps {
  plan?: string
  isYearly?: boolean
  changePlan?: boolean
  pendingPlan?: string
  isFreemiumUser?: boolean
}

interface MonthlyProps {
  plan?: string
  isMonthly?: boolean
  changePlan?: boolean
  pendingPlan?: string
  isFreemiumUser?: boolean
}

type PlanId = 'storage' | 'sharing' | 'expert' | 'free'

const CHANGETITLE = 'Change Your Plan'
const UPGRADETITLE = 'Upgrade Your Plan'
const DOWNGRADETITLE = 'Downgrade Your Plan'
const CHANGECONTENT = 'You are about to change your plan to from '
const UPGRADECONTENT = 'Great choice! With this updgrade, you will be gaining access to essential features like '
const DOWNGRADECONTENT =
  "We're sorry to see that you'd like to downgrade your plan. While we understand, you should be aware that you will be losing access to essential features like "
const HOSTINGFEATURE = 'embed links for hosting'
const SHARINGFEATURE = 'sharing, photo editing, and video playback'

function isEligibleForFreeTrial(subscriptionStatus?: SubscriptionStatus | null, freePlanEnabled?: boolean, membershipPage?: boolean): boolean {
  // If freePlanEnabled is true, user is not eligible for free trial or on membership page
  if (freePlanEnabled || membershipPage) {
    return false
  }

  // If subscription status is one of the ineligible statuses, user is not eligible for free trial
  const ineligibleStatuses = [
    SubscriptionStatus.Predeactivated,
    SubscriptionStatus.Deactivated,
    SubscriptionStatus.Expired,
    SubscriptionStatus.Canceled
  ]

  if (subscriptionStatus && ineligibleStatuses.includes(subscriptionStatus)) {
    return false
  }

  // User is eligible for free trial if they don't have an ineligible status and freePlanEnabled is false
  return true
}

function MonthlyPlans({
  plan = undefined,
  isMonthly = false,
  changePlan = false,
  pendingPlan = undefined,
  isFreemiumUser = false
}: Readonly<MonthlyProps>) {
  // Memoize the formatted date to avoid creating new Date() on every render
  const paymentDate = useMemo(() => {
    return new Date().toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    })
  }, [])

  const userProfile = useQuery(UserProfileDocument, { nextFetchPolicy: 'cache-and-network' })
  const location = useLocation()
  const params = new URLSearchParams(location.search)
  const { pathname } = location
  const subscriptionPage = pathname.includes('/subscription')
  const settingsPage = pathname.includes('/settings')
  const freePlanEnabled = useFlag('freePlanEnabled')
  const allowFreePlan = userProfile.data?.userProfile?.allowFreePlan ?? true
  const subscriptionStatus = userProfile.data?.userProfile?.subscription?.subscription?.status
  const membershipPage = pathname.includes('/settings')
  const displayFreePlan = freePlanEnabled && allowFreePlan && !settingsPage && subscriptionStatus !== SubscriptionStatus.Predeactivated
  const isEligibleForTrial = isEligibleForFreeTrial(subscriptionStatus, freePlanEnabled, settingsPage)
  const { createAlert } = useAlerts()

  const paymentMethod = userProfile.data?.userProfile?.subscription?.subscription?.account ?? 'Free'
  const isRecurly = ['recurly'].includes(paymentMethod.toLowerCase())
  const subscriptionId = userProfile.data?.userProfile?.subscription?.subscription?.id
  const subscription = useQuery(SubscriptionDocument, { skip: !subscriptionId && !isRecurly, variables: { subscriptionId: subscriptionId! } })
  const isRecurring = userProfile.data?.userProfile?.subscription?.recurring

  const currentTermEndsAt = subscription.data?.subscriptions?.subscription?.recurlySubscription?.currentTermEndsAt

  const formattedDate = currentTermEndsAt
    ? new Date(currentTermEndsAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    : 'date not available'

  // Check for promo or coupon query params
  const promo = params.get('promo')
  const coupon = params.get('coupon')
  const couponQuery = promo || coupon ? `&coupon=${promo ?? coupon}` : ''
  const subscriptionNextPageUrl = subscriptionPage ? '/subscription/payment' : '/auth/register'
  const { trackEvent } = useGAEvent()

  const [planName, setPlanName] = useState<string>()
  const [price, setPrice] = useState<string>()
  const [contactUs, setContactUs] = useState<boolean>(false)
  const [upgradeTitle, setUpgradeTitle] = useState<string>()
  const [upgradeContent, setUpgradeContent] = useState<string>()
  const [boldContent, setBoldContent] = useState<string>()
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState<boolean>(false)
  const onUpgradeModalClose = () => {
    setIsUpgradeModalOpen(false)
  }
  const [isDowngradeFreePlanModalOpen, setIsDowngradeFreePlanModalOpen] = useState<boolean>(false)
  const onDowngradeFreePlanModalClose = () => {
    setIsDowngradeFreePlanModalOpen(false)
  }
  const [showPaymentModal, setShowPaymentModal] = useState<boolean>(false)
  const handlePaymentClose = () => {
    setShowPaymentModal(false)
  }

  const isPlanAnUpgrade = useCallback(
    (selectedPlan: string) => {
      const currentPlan = userProfile.data?.userProfile?.subscription?.planName?.split('-')[0] ?? 'Free'

      if ((selectedPlan === 'expert' && currentPlan === 'expert') || selectedPlan === currentPlan) {
        setUpgradeTitle(CHANGETITLE)
        setUpgradeContent(CHANGECONTENT)
        setBoldContent(`annual to monthly`)
        setContactUs(false)
      } else if (selectedPlan === 'expert' && currentPlan !== 'expert') {
        setUpgradeTitle(UPGRADETITLE)
        setUpgradeContent(UPGRADECONTENT)
        setBoldContent(HOSTINGFEATURE)
        setContactUs(false)
      } else if (selectedPlan === 'sharing' && currentPlan === 'storage') {
        setUpgradeTitle(UPGRADETITLE)
        setUpgradeContent(UPGRADECONTENT)
        setBoldContent(SHARINGFEATURE)
        setContactUs(false)
      } else if (selectedPlan !== 'expert' && currentPlan === 'expert') {
        setUpgradeTitle(DOWNGRADETITLE)
        setUpgradeContent(DOWNGRADECONTENT)
        setBoldContent(HOSTINGFEATURE)
        setContactUs(true)
      } else {
        setUpgradeTitle(DOWNGRADETITLE)
        setUpgradeContent(DOWNGRADECONTENT)
        setBoldContent(SHARINGFEATURE)
        setContactUs(true)
      }
    },
    [userProfile.data?.userProfile?.subscription?.planName]
  )

  const onClickPlan = useCallback(
    (id: PlanId, cycle: string, selectedPrice: number, itemId: string) => {
      trackEvent('select_item', {
        ecommerce: {
          item_list_id: itemId,
          item_list_name: `${id}-${cycle}`,
          items: [{ item_id: `${id}_${cycle}`, item_name: `${id} ${cycle}`, selectedPrice }]
        }
      })

      if (isRecurly && changePlan) {
        if (!isRecurring) {
          createAlert(
            'You cannot change plans while your subscription is pending cancellation. Please reactivate your subscription first.',
            'warning'
          )
          return
        }
        isPlanAnUpgrade(id)
        setIsUpgradeModalOpen(true)
        setPlanName(id)
        setPrice(selectedPrice.toString())
        return
      }
      if (isFreemiumUser && membershipPage) {
        if (id === 'storage') {
          setIsDowngradeFreePlanModalOpen(true)
        } else {
          setShowPaymentModal(true)
          setPlanName(id)
          setPrice(selectedPrice.toString())
        }
        return
      }
      if (plan === 'no-plan') {
        const url = `subscription/payment?plan_name=${id}&billing_cycle=${cycle}${couponQuery}`
        window.location.href = url
        return
      }

      const url = `${subscriptionNextPageUrl}?plan_name=${id}&billing_cycle=${cycle}${couponQuery}`
      window.location.href = url
    },
    [
      trackEvent,
      isRecurly,
      changePlan,
      isFreemiumUser,
      membershipPage,
      plan,
      subscriptionNextPageUrl,
      couponQuery,
      isRecurring,
      isPlanAnUpgrade,
      createAlert
    ]
  )

  // capture event for page view
  useEffectOnce(() => {
    trackEvent('view_item_list', {
      ecommerce: {
        item_list_id: 'monthly plans',
        item_list_name: 'monthly plans',
        items: [
          {
            item_id: '562dcf87-1976-4404-ab7a-2fbba530f790',
            item_name: 'free',
            price: 0
          },
          {
            item_id: 'f4041bef-924b-451b-88dc-3a8aba90ec5e',
            item_name: 'sharing_monthly',
            price: 8
          },
          {
            item_id: 'ce2dd8aa-69c7-47f4-ae4d-9f09e094f13c',
            item_name: 'hosting_monthly',
            price: 13
          }
        ]
      }
    })
  })

  return (
    <>
      <PlansContainer>
        {displayFreePlan ? (
          <PlanCardWrapper>
            {plan === 'free' || pendingPlan === 'free' ? (
              <CurrentPlanCard>
                <CardHead>Free</CardHead>
                <CardStorage>Essentials to get started</CardStorage>
                <CardPrice>$0</CardPrice>
                <CardTerm>per month</CardTerm>
                <PaddingActions>
                  <CurrentPlan disabled data-testid="current-plan">
                    {pendingPlan?.includes('free') ? 'Pending Plan' : 'Current Plan'}
                  </CurrentPlan>
                </PaddingActions>
                <FeatureList plan="free" />
              </CurrentPlanCard>
            ) : (
              <StyledCard>
                <CardHead>Free</CardHead>
                <CardStorage>Essentials to get started</CardStorage>
                <CardPrice>$0</CardPrice>
                <CardTerm>per month</CardTerm>
                <PaddingActions>
                  <StyledButton
                    data-test="plans-free"
                    fullWidth
                    variant="contained"
                    onClick={() => {
                      onClickPlan('free', 'NA', 0, '562dcf87-1976-4404-ab7a-2fbba530f790')
                    }}
                  >
                    Select Plan
                  </StyledButton>
                </PaddingActions>
                <FeatureList plan="free" />
              </StyledCard>
            )}
            <BlackText>
              <Box component="span">New sign-ups only</Box>
            </BlackText>
          </PlanCardWrapper>
        ) : (
          <PlanCardWrapper>
            {(plan === 'storage' && isMonthly) || pendingPlan === 'storage-monthly' ? (
              <CurrentPlanCard>
                <CardHead>My Bucket</CardHead>
                <CardStorage>1 TB of storage</CardStorage>
                <CardPrice>$5</CardPrice>
                <CardTerm>per month</CardTerm>
                <PaddingActions>
                  <CurrentPlan disabled data-testid="current-plan">
                    {pendingPlan?.includes('storage') ? 'Pending Plan' : 'Current Plan'}
                  </CurrentPlan>
                </PaddingActions>
                <FeatureList plan="storage" />
              </CurrentPlanCard>
            ) : (
              <StyledCard>
                <CardHead>My Bucket</CardHead>
                <CardStorage>1 TB of storage</CardStorage>
                <CardPrice>$5</CardPrice>
                <CardTerm>per month</CardTerm>
                <PaddingActions>
                  <StyledButton
                    data-test="storage-monthly"
                    fullWidth
                    variant="contained"
                    onClick={() => {
                      onClickPlan('storage', 'monthly', 5, '6f0ba9cb-7dcf-4f89-9fda-ba430662b006')
                    }}
                  >
                    Select Plan
                  </StyledButton>
                </PaddingActions>
                <FeatureList plan="storage" />
              </StyledCard>
            )}
            {(!subscriptionPage && plan === 'storage' && !isMonthly) || pendingPlan === 'storage-annual' ? (
              <BlackLink>
                <Box data-testid="black-link-annual-storage-current" component="span" color="black">
                  {pendingPlan === 'storage-annual' ? 'Pending Plan' : 'Current Plan'}
                </Box>
              </BlackLink>
            ) : (
              <BlackLink
                onClick={() => {
                  onClickPlan('storage', 'annual', 50, '9901c53a-671a-41f2-9f4d-c22d013400bf')
                }}
              >
                <Box data-testid="black-link-annual-storage" component="span">
                  Save another $10 by paying yearly
                </Box>
              </BlackLink>
            )}
          </PlanCardWrapper>
        )}
        <PlanCardWrapper>
          {(plan === 'sharing' && isMonthly) || pendingPlan === 'sharing-monthly' ? (
            <CurrentPlanCard>
              <CurrentPlanRecommended>Most Popular</CurrentPlanRecommended>
              <CardHead>Group Buckets</CardHead>
              <CardStorage>1 TB of storage + sharing</CardStorage>
              <CardPrice>$8</CardPrice>
              <CardTerm>per month</CardTerm>
              <PaddingActions>
                <CurrentPlan disabled data-testid="current-plan">
                  {pendingPlan?.includes('sharing') ? 'Pending Plan' : 'Current Plan'}
                </CurrentPlan>
              </PaddingActions>
              <FeatureList plan="sharing" />
            </CurrentPlanCard>
          ) : (
            <PopularCard>
              <Recommended>Most Popular</Recommended>
              <CardHead>Group Buckets</CardHead>
              <CardStorage>1 TB of storage + sharing</CardStorage>
              <CardPrice>$8</CardPrice>
              <CardTerm>per month</CardTerm>
              <PaddingActions>
                <PopularButton
                  data-test="plans-sharing-monthly"
                  fullWidth
                  variant="contained"
                  onClick={() => {
                    if (isEligibleForTrial) {
                      onClickPlan('sharing', 'trial', 0, '3e6d9700-24cf-4a70-9b22-ece1fab471b1')
                    } else {
                      onClickPlan('sharing', 'monthly', 8, 'f4041bef-924b-451b-88dc-3a8aba90ec5e')
                    }
                  }}
                >
                  {isEligibleForTrial ? 'Start 7-day Free Trial' : 'Select Plan'}
                </PopularButton>
              </PaddingActions>
              <FeatureList plan="sharing" />
            </PopularCard>
          )}
          {(!subscriptionPage && plan === 'sharing' && !isMonthly) || pendingPlan === 'sharing-annual' ? (
            <BlackLink>
              <Box data-testid="black-link-annual-sharing-current" component="span" color="black">
                {pendingPlan === 'sharing-annual' ? 'Pending Plan' : 'Current Plan'}
              </Box>
            </BlackLink>
          ) : (
            <BlackLink
              onClick={() => {
                onClickPlan('sharing', 'annual', 80, '96cda310-2859-4f4c-bbc6-f6f4c5d09296')
              }}
            >
              <Box component="span">Save another $16 by paying yearly</Box>
            </BlackLink>
          )}
        </PlanCardWrapper>
        <PlanCardWrapper>
          {(plan === 'expert' && isMonthly) || pendingPlan === 'expert-monthly' ? (
            <CurrentPlanCard>
              <CardHead>Hosting</CardHead>
              <CardStorage>Embedded sharing</CardStorage>
              <CardPrice>$13</CardPrice>
              <CardTerm>per month</CardTerm>
              <PaddingActions>
                <CurrentPlan disabled data-testid="current-plan">
                  {pendingPlan?.includes('expert') ? 'Pending Plan' : 'Current Plan'}
                </CurrentPlan>
              </PaddingActions>
              <FeatureList plan="expert" />
            </CurrentPlanCard>
          ) : (
            <StyledCard>
              <CardHead>Hosting</CardHead>
              <CardStorage>Embedded sharing</CardStorage>
              <CardPrice>$13</CardPrice>
              <CardTerm>per month</CardTerm>
              <PaddingActions>
                <StyledButton
                  data-test="plans-expert-monthly"
                  fullWidth
                  variant="contained"
                  onClick={() => {
                    onClickPlan('expert', 'monthly', 13, 'ce2dd8aa-69c7-47f4-ae4d-9f09e094f13c')
                  }}
                >
                  Select Plan
                </StyledButton>
              </PaddingActions>
              <FeatureList plan="expert" />
            </StyledCard>
          )}
          {(!subscriptionPage && plan === 'expert' && !isMonthly) || pendingPlan === 'expert-annual' ? (
            <BlackLink>
              <Box data-testid="black-link-annual-hosting-current" component="span" color="black">
                {pendingPlan === 'expert-annual' ? 'Pending Plan' : 'Current Plan'}
              </Box>
            </BlackLink>
          ) : (
            <BlackLink
              onClick={() => {
                onClickPlan('expert', 'annual', 140, '82ce8670-7abc-4fa4-8868-bce86e5da8ef')
              }}
            >
              <Box data-testid="black-link-annual-hosting" component="span">
                Save another $16 by paying yearly
              </Box>
            </BlackLink>
          )}
        </PlanCardWrapper>
      </PlansContainer>
      <UpgradeChangeModal
        isOpen={isUpgradeModalOpen}
        onClose={onUpgradeModalClose}
        title={upgradeTitle}
        content={upgradeContent}
        boldContent={boldContent}
        price={price}
        planName={planName ?? 'free'}
        billingCycle="monthly"
        contactUs={contactUs}
        currentTermEndsAt={formattedDate}
      />
      <DowngradeFreePlanModal isOpen={isDowngradeFreePlanModalOpen} onClose={onDowngradeFreePlanModalClose} planCycle="monthly" />
      <Payment
        open={showPaymentModal}
        onClose={handlePaymentClose}
        planName={planName}
        billingCycle="monthly"
        total={price}
        buttonText="Complete Purchase"
        content={`Your first payment of $${price} will be on ${paymentDate} and billing will recur on a monthly basis. You can cancel anytime.`}
        source="settings"
      />
    </>
  )
}

function YearlyPlans({ plan = undefined, isYearly = false, changePlan = false, pendingPlan, isFreemiumUser = false }: Readonly<YearlyProps>) {
  // Memoize the formatted date to avoid creating new Date() on every render
  const paymentDate = useMemo(() => {
    return new Date().toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    })
  }, [])

  const userProfile = useQuery(UserProfileDocument, { nextFetchPolicy: 'cache-and-network' })
  const location = useLocation()
  const params = new URLSearchParams(location.search)
  const { pathname } = location
  const subscriptionPage = pathname.includes('/subscription')
  const settingsPage = pathname.includes('/settings')
  const freePlanEnabled = useFlag('freePlanEnabled')
  const allowFreePlan = userProfile.data?.userProfile?.allowFreePlan ?? true
  const subscriptionStatus = userProfile.data?.userProfile?.subscription?.subscription?.status
  const displayFreePlan = freePlanEnabled && allowFreePlan && !settingsPage && subscriptionStatus !== SubscriptionStatus.Predeactivated
  const { createAlert } = useAlerts()

  const paymentMethod = userProfile.data?.userProfile?.subscription?.subscription?.account ?? 'Free'
  const isRecurly = ['recurly'].includes(paymentMethod.toLowerCase())
  const membershipPage = pathname.includes('/settings')
  const subscriptionId = userProfile.data?.userProfile?.subscription?.subscription?.id
  const subscription = useQuery(SubscriptionDocument, { skip: !subscriptionId && !isRecurly, variables: { subscriptionId: subscriptionId! } })
  const isRecurring = userProfile.data?.userProfile?.subscription?.recurring

  const currentTermEndsAt = subscription.data?.subscriptions?.subscription?.recurlySubscription?.currentTermEndsAt

  const formattedDate = currentTermEndsAt
    ? new Date(currentTermEndsAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    : 'date not available'

  // Check for promo or coupon query params
  const promo = params.get('promo')
  const coupon = params.get('coupon')
  const couponQuery = promo || coupon ? `&coupon=${promo ?? coupon}` : ''
  const subscriptionNextPageUrl = subscriptionPage ? '/subscription/payment' : '/auth/register'
  const { trackEvent } = useGAEvent()

  const [planName, setPlanName] = useState<string>()
  const [price, setPrice] = useState<string>()
  const [contactUs, setContactUs] = useState<boolean>(false)
  const [upgradeTitle, setUpgradeTitle] = useState<string>()
  const [upgradeContent, setUpgradeContent] = useState<string>()
  const [boldContent, setBoldContent] = useState<string>()
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState<boolean>(false)
  const onUpgradeModalClose = () => {
    setIsUpgradeModalOpen(false)
  }
  const [isDowngradeFreePlanModalOpen, setIsDowngradeFreePlanModalOpen] = useState<boolean>(false)
  const onDowngradeFreePlanModalClose = () => {
    setIsDowngradeFreePlanModalOpen(false)
  }
  const [showPaymentModal, setShowPaymentModal] = useState<boolean>(false)
  const handlePaymentClose = () => {
    setShowPaymentModal(false)
  }

  const isPlanAnUpgrade = useCallback(
    (selectedPlan: string) => {
      const currentPlan = userProfile.data?.userProfile?.subscription?.planName?.split('-')[0] ?? 'Free'

      if ((selectedPlan === 'expert' && currentPlan === 'expert') || selectedPlan === currentPlan) {
        setUpgradeTitle(CHANGETITLE)
        setUpgradeContent(CHANGECONTENT)
        setBoldContent(`monthly to annual`)
        setContactUs(false)
      } else if (selectedPlan === 'expert' && currentPlan !== 'expert') {
        setUpgradeTitle(UPGRADETITLE)
        setUpgradeContent(UPGRADECONTENT)
        setBoldContent(HOSTINGFEATURE)
        setContactUs(false)
      } else if (selectedPlan === 'sharing' && currentPlan === 'storage') {
        setUpgradeTitle(UPGRADETITLE)
        setUpgradeContent(UPGRADECONTENT)
        setBoldContent(SHARINGFEATURE)
        setContactUs(false)
      } else if (selectedPlan !== 'expert' && currentPlan === 'expert') {
        setUpgradeTitle(DOWNGRADETITLE)
        setUpgradeContent(DOWNGRADECONTENT)
        setBoldContent(HOSTINGFEATURE)
        setContactUs(true)
      } else {
        setUpgradeTitle(DOWNGRADETITLE)
        setUpgradeContent(DOWNGRADECONTENT)
        setBoldContent(SHARINGFEATURE)
        setContactUs(true)
      }
    },
    [userProfile.data?.userProfile?.subscription?.planName]
  )

  const onClickPlan = useCallback(
    (id: 'storage' | 'sharing' | 'expert' | 'free', cycle: string, selectedPrice: number, itemId: string) => {
      trackEvent('select_item', {
        ecommerce: {
          item_list_id: itemId,
          item_list_name: `${id}-${cycle}`,
          items: [{ item_id: `${id}_${cycle}`, item_name: `${id} ${cycle}`, selectedPrice }]
        }
      })
      if (isRecurly && changePlan) {
        // Prevent upgrade/downgrade if subscription is not recurring (cancelled)
        if (!isRecurring) {
          createAlert(
            'You cannot change plans while your subscription is pending cancellation. Please reactivate your subscription first.',
            'warning'
          )
          return
        }
        isPlanAnUpgrade(id)
        setIsUpgradeModalOpen(true)
        setPlanName(id)
        setPrice(selectedPrice.toString())
        return
      }
      if (isFreemiumUser && membershipPage) {
        if (id === 'storage') {
          setIsDowngradeFreePlanModalOpen(true)
        } else {
          setShowPaymentModal(true)
          setPlanName(id)
          setPrice(selectedPrice.toString())
        }
        return
      }

      const url = `${subscriptionNextPageUrl}?plan_name=${id}&billing_cycle=${cycle}${couponQuery}`

      window.location.href = url
    },
    [
      trackEvent,
      isRecurly,
      changePlan,
      isFreemiumUser,
      membershipPage,
      subscriptionNextPageUrl,
      couponQuery,
      isRecurring,
      isPlanAnUpgrade,
      createAlert
    ]
  )

  // capture event for page view
  useEffectOnce(() => {
    trackEvent('view_item_list', {
      ecommerce: {
        item_list_id: 'annual plans',
        item_list_name: 'annual plans',
        items: [
          {
            item_id: '562dcf87-1976-4404-ab7a-2fbba530f790',
            item_name: 'free',
            price: 0
          },
          {
            item_id: '96cda310-2859-4f4c-bbc6-f6f4c5d09296',
            item_name: 'sharing_annual',
            price: 80
          },
          {
            item_id: '82ce8670-7abc-4fa4-8868-bce86e5da8ef',
            item_name: 'hosting_annual',
            price: 140
          }
        ]
      }
    })
  })

  return (
    <>
      <PlansContainer>
        {displayFreePlan ? (
          <PlanCardWrapper>
            {plan === 'free' || pendingPlan === 'free' ? (
              <CurrentPlanCard>
                <CardHead>Free</CardHead>
                <CardStorage>Essentials to get started</CardStorage>
                <CardPrice>$0</CardPrice>
                <CardTerm>per year</CardTerm>
                <PaddingActions>
                  <CurrentPlan disabled data-testid="current-plan">
                    {pendingPlan?.includes('free') ? 'Pending Plan' : 'Current Plan'}
                  </CurrentPlan>
                </PaddingActions>
                <FeatureList plan="free" />
              </CurrentPlanCard>
            ) : (
              <StyledCard>
                <CardHead>Free</CardHead>
                <CardStorage>Essentials to get started</CardStorage>
                <CardPrice>$0</CardPrice>
                <CardTerm>per year</CardTerm>
                <PaddingActions>
                  <StyledButton
                    data-test="plans-yearly-sharing"
                    fullWidth
                    variant="contained"
                    onClick={() => {
                      onClickPlan('free', 'NA', 0, '562dcf87-1976-4404-ab7a-2fbba530f790')
                    }}
                  >
                    Select Plan
                  </StyledButton>
                </PaddingActions>
                <FeatureList plan="free" />
              </StyledCard>
            )}
            <BlackText>
              <Box component="span">New sign-ups only</Box>
            </BlackText>
          </PlanCardWrapper>
        ) : (
          <PlanCardWrapper>
            {(plan === 'storage' && isYearly) || pendingPlan === 'storage-annual' ? (
              <CurrentPlanCard>
                <CardHead>My Bucket</CardHead>
                <CardStorage>1 TB of storage</CardStorage>
                <CardPrice>$50</CardPrice>
                <CardTerm>per year</CardTerm>
                <PaddingActions>
                  <CurrentPlan disabled data-testid="current-plan">
                    {pendingPlan?.includes('storage') ? 'Pending Plan' : 'Current Plan'}
                  </CurrentPlan>
                </PaddingActions>
                <FeatureList plan="storage" />
              </CurrentPlanCard>
            ) : (
              <StyledCard>
                <CardHead>My Bucket</CardHead>
                <CardStorage>1 TB of storage</CardStorage>
                <CardPrice>$50</CardPrice>
                <CardTerm>per year</CardTerm>
                <PaddingActions>
                  <StyledButton
                    data-test="storage-annual"
                    fullWidth
                    variant="contained"
                    onClick={() => {
                      onClickPlan('storage', 'annual', 50, '9901c53a-671a-41f2-9f4d-c22d013400bf')
                    }}
                  >
                    Select Plan
                  </StyledButton>
                </PaddingActions>
                <FeatureList plan="storage" />
              </StyledCard>
            )}
            <CardSavings>
              You <Box component="span">save $10</Box> vs a monthly payment
            </CardSavings>
          </PlanCardWrapper>
        )}
        <PlanCardWrapper>
          {(plan === 'sharing' && isYearly) || pendingPlan === 'sharing-annual' ? (
            <CurrentPlanCard>
              <CurrentPlanRecommended>Most Popular</CurrentPlanRecommended>
              <CardHead>Group Buckets</CardHead>
              <CardStorage>1 TB of storage + sharing</CardStorage>
              <CardPrice>$80</CardPrice>
              <CardTerm>per year</CardTerm>
              <PaddingActions>
                <CurrentPlan disabled data-testid="current-plan">
                  {pendingPlan?.includes('sharing') ? 'Pending Plan' : 'Current Plan'}
                </CurrentPlan>
              </PaddingActions>
              <FeatureList plan="sharing" />
            </CurrentPlanCard>
          ) : (
            <PopularCard>
              <Recommended>Most Popular</Recommended>
              <CardHead>Group Buckets</CardHead>
              <CardStorage>1 TB of storage + sharing</CardStorage>
              <CardPrice>$80</CardPrice>
              <CardTerm>per year</CardTerm>
              <PaddingActions>
                <PopularButton
                  data-test="plans-yearly-sharing"
                  fullWidth
                  variant="contained"
                  onClick={() => {
                    onClickPlan('sharing', 'annual', 80, '96cda310-2859-4f4c-bbc6-f6f4c5d09296')
                  }}
                >
                  Select Plan
                </PopularButton>
              </PaddingActions>
              <FeatureList plan="sharing" />
            </PopularCard>
          )}
          <CardSavings>
            You <Box component="span">save $16</Box> vs a monthly payment
          </CardSavings>
        </PlanCardWrapper>
        <PlanCardWrapper>
          {(plan === 'expert' && isYearly) || pendingPlan === 'expert-annual' ? (
            <CurrentPlanCard>
              <CardHead>Hosting</CardHead>
              <CardStorage>Embedded sharing</CardStorage>
              <CardPrice>$140</CardPrice>
              <CardTerm>per year</CardTerm>
              <PaddingActions>
                <CurrentPlan disabled data-testid="current-plan">
                  {pendingPlan === 'expert-annual' ? 'Pending Plan' : 'Current Plan'}
                </CurrentPlan>
              </PaddingActions>
              <FeatureList plan="expert" />
            </CurrentPlanCard>
          ) : (
            <StyledCard>
              <CardHead>Hosting</CardHead>
              <CardStorage>Embedded sharing</CardStorage>
              <CardPrice>$140</CardPrice>
              <CardTerm>per year</CardTerm>
              <PaddingActions>
                <StyledButton
                  data-testid="plans-annual-hosting"
                  fullWidth
                  variant="outlined"
                  onClick={() => {
                    onClickPlan('expert', 'annual', 140, '82ce8670-7abc-4fa4-8868-bce86e5da8ef')
                  }}
                >
                  Select Plan
                </StyledButton>
              </PaddingActions>
              <FeatureList plan="expert" />
            </StyledCard>
          )}
          <CardSavings>
            You <Box component="span">save $16</Box> vs a monthly payment
          </CardSavings>
        </PlanCardWrapper>
      </PlansContainer>
      <UpgradeChangeModal
        isOpen={isUpgradeModalOpen}
        onClose={onUpgradeModalClose}
        title={upgradeTitle}
        content={upgradeContent}
        boldContent={boldContent}
        contactUs={contactUs}
        price={price}
        planName={planName ?? 'free'}
        billingCycle="annual"
        currentTermEndsAt={formattedDate}
      />
      <DowngradeFreePlanModal isOpen={isDowngradeFreePlanModalOpen} onClose={onDowngradeFreePlanModalClose} planCycle="annual" />
      <Payment
        open={showPaymentModal}
        onClose={handlePaymentClose}
        planName={planName}
        billingCycle="annual"
        total={price}
        buttonText="Complete Purchase"
        content={`Your first payment of $${price} will be on ${paymentDate} and billing will recur on an annual basis. You can cancel anytime.`}
        source="settings"
      />
    </>
  )
}

export { MonthlyPlans, YearlyPlans }
